import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  AttendanceMetrics,
  AttendantMetric,
  AttendantRanking,
  ImportHistory,
  MetricsQuery,
  AttendanceSnapshot,
  SnapshotComparison,
  RawRecordsCount,
  PaginatedRawRecords,
  CreateSnapshotDto,
  DeleteRawRecordsDto,
  DeleteRawRecordsResponse,
  RawRecordsFilters,
  MessageHistogram,
  Attendant,
  SnapshotDetail,
} from '@/types/attendance-metrics'

const QUERY_KEYS = {
  metrics: ['attendance-metrics'] as const,
  attendants: ['attendance-metrics', 'attendants'] as const,
  attendantsList: ['attendance-metrics', 'attendants-list'] as const,
  rankings: ['attendance-metrics', 'rankings'] as const,
  imports: ['attendance-metrics', 'imports'] as const,
  import: (id: string) => ['attendance-metrics', 'imports', id] as const,
  snapshots: ['attendance-metrics', 'snapshots'] as const,
  snapshot: (id: string) => ['attendance-metrics', 'snapshots', id] as const,
  comparison: (id1: string, id2: string) => ['attendance-metrics', 'snapshots', 'compare', id1, id2] as const,
  rawRecords: (filters: RawRecordsFilters) => ['attendance-metrics', 'raw-records', filters] as const,
  rawRecordsCount: ['attendance-metrics', 'raw-records', 'count'] as const,
  messageHistogram: (startDate: string, endDate: string, interval?: string) =>
    ['attendance-metrics', 'messages', 'histogram', { startDate, endDate, interval }] as const,
}

// GET /attendance-metrics - Main metrics with optional comparison
export function useAttendanceMetrics(filters: MetricsQuery) {
  return useQuery<AttendanceMetrics>({
    queryKey: [...QUERY_KEYS.metrics, filters],
    queryFn: async () => {
      const { data } = await api.get<AttendanceMetrics>('/attendance-metrics', {
        params: filters,
      })
      return data
    },
    enabled: !!filters.startDate && !!filters.endDate,
  })
}

// GET /attendance-metrics/attendants - Metrics by attendant
export function useAttendantMetrics(filters: MetricsQuery) {
  return useQuery<AttendantMetric[]>({
    queryKey: [...QUERY_KEYS.attendants, filters],
    queryFn: async () => {
      const { data } = await api.get<AttendantMetric[]>('/attendance-metrics/attendants', {
        params: filters,
      })
      return data
    },
    enabled: !!filters.startDate && !!filters.endDate,
  })
}

// GET /attendance-metrics/rankings - Rankings by metric
export function useAttendantRankings(filters: MetricsQuery) {
  return useQuery<AttendantRanking>({
    queryKey: [...QUERY_KEYS.rankings, filters],
    queryFn: async () => {
      const { data } = await api.get<AttendantRanking>('/attendance-metrics/rankings', {
        params: filters,
      })
      return data
    },
    enabled: !!filters.startDate && !!filters.endDate,
  })
}

// GET /attendance-metrics/imports - Import history list
export function useImportHistory() {
  return useQuery<ImportHistory[]>({
    queryKey: QUERY_KEYS.imports,
    queryFn: async () => {
      const { data } = await api.get<ImportHistory[]>('/attendance-metrics/imports')
      return data
    },
  })
}

// GET /attendance-metrics/imports/:id - Single import job (polling)
export function useImportJob(jobId: string, options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery<ImportHistory>({
    queryKey: QUERY_KEYS.import(jobId),
    queryFn: async () => {
      const { data } = await api.get<ImportHistory>(`/attendance-metrics/imports/${jobId}`)
      return data
    },
    enabled: options?.enabled ?? !!jobId,
    refetchInterval: options?.refetchInterval ?? 2000, // Poll every 2s by default
    refetchIntervalInBackground: false,
  })
}

// POST /attendance-metrics/imports - Start new import
export function useStartImport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { startDate: string; endDate: string; forceReimport?: boolean }) => {
      const { data: result } = await api.post<ImportHistory>('/attendance-metrics/imports', data)
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.imports })
    },
  })
}

// DELETE /attendance-metrics/imports/:id - Cancel import
export function useCancelImport() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (jobId: string) => {
      await api.delete(`/attendance-metrics/imports/${jobId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.imports })
    },
  })
}

// GET /attendance-metrics/export - Export CSV (returns blob)
export function useExportMetrics() {
  return useMutation({
    mutationFn: async (filters: MetricsQuery) => {
      const { data } = await api.get('/attendance-metrics/export', {
        params: filters,
        responseType: 'blob',
      })
      // Create download link
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `attendance-metrics-${filters.startDate}-${filters.endDate}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
  })
}

// GET /attendance-metrics/snapshots - List all snapshots
export function useSnapshots() {
  return useQuery<AttendanceSnapshot[]>({
    queryKey: QUERY_KEYS.snapshots,
    queryFn: async () => {
      const { data } = await api.get<AttendanceSnapshot[]>('/attendance-metrics/snapshots')
      return data
    },
  })
}

// POST /attendance-metrics/snapshots - Create new snapshot
export function useCreateSnapshot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (dto: CreateSnapshotDto) => {
      const { data } = await api.post<AttendanceSnapshot>('/attendance-metrics/snapshots', dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snapshots })
    },
  })
}

// DELETE /attendance-metrics/snapshots/:id - Delete snapshot
export function useDeleteSnapshot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (snapshotId: string) => {
      await api.delete(`/attendance-metrics/snapshots/${snapshotId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snapshots })
    },
  })
}

// GET /attendance-metrics/snapshots/compare - Compare two snapshots
export function useCompareSnapshots(id1: string, id2: string, options?: { enabled?: boolean }) {
  return useQuery<SnapshotComparison>({
    queryKey: QUERY_KEYS.comparison(id1, id2),
    queryFn: async () => {
      const { data } = await api.get<SnapshotComparison>('/attendance-metrics/snapshots/compare', {
        params: { snapshot1Id: id1, snapshot2Id: id2 },
      })
      return data
    },
    enabled: options?.enabled ?? (!!id1 && !!id2 && id1 !== id2),
  })
}

// GET /attendance-metrics/raw-records - Paginated raw records with filters
export function useRawRecords(filters: RawRecordsFilters = {}) {
  const { page = 1, limit = 20, ticketId, protocolId, userId, startDate, endDate, sortOrder, senderType } = filters

  return useQuery<PaginatedRawRecords>({
    queryKey: QUERY_KEYS.rawRecords(filters),
    queryFn: async () => {
      const params: Record<string, unknown> = { page, limit }

      if (ticketId) params.ticketId = ticketId
      if (protocolId) params.protocolId = protocolId
      if (userId) params.userId = userId
      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate
      if (sortOrder) params.sortOrder = sortOrder
      if (senderType && senderType !== 'all') params.senderType = senderType

      const { data } = await api.get<PaginatedRawRecords>('/attendance-metrics/raw-records', {
        params,
      })
      return data
    },
  })
}

// GET /attendance-metrics/raw-records/count - Count raw records
export function useRawRecordsCount() {
  return useQuery<RawRecordsCount>({
    queryKey: QUERY_KEYS.rawRecordsCount,
    queryFn: async () => {
      const { data } = await api.get<RawRecordsCount>('/attendance-metrics/raw-records/count')
      return data
    },
  })
}

// DELETE /attendance-metrics/raw-records - Delete raw records
export function useDeleteRawRecords() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (dto: DeleteRawRecordsDto) => {
      // Convert datetime-local format to ISO 8601 if needed
      const beforeDate = dto.beforeDate.includes('T') && !dto.beforeDate.endsWith('Z')
        ? new Date(dto.beforeDate).toISOString()
        : dto.beforeDate

      const { data } = await api.delete<DeleteRawRecordsResponse>('/attendance-metrics/raw-records', {
        data: { beforeDate },
      })
      return data
    },
    onSuccess: () => {
      // Invalidate all raw records queries
      queryClient.invalidateQueries({ queryKey: ['attendance-metrics', 'raw-records'] })
      // Also invalidate metrics since data may have changed
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.metrics })
    },
  })
}

// GET /attendance-metrics/messages/histogram - Message histogram for charts
export function useMessageHistogram(
  startDate: string,
  endDate: string,
  interval: 'hour' | 'day' = 'day',
  options?: { enabled?: boolean }
) {
  return useQuery<MessageHistogram>({
    queryKey: QUERY_KEYS.messageHistogram(startDate, endDate, interval),
    queryFn: async () => {
      const { data } = await api.get<MessageHistogram>('/attendance-metrics/messages/histogram', {
        params: { startDate, endDate, interval },
      })
      return data
    },
    enabled: options?.enabled ?? (!!startDate && !!endDate),
  })
}

// GET /attendance-metrics/attendants-list - List attendants with messages
export function useAttendantsList() {
  return useQuery<Attendant[]>({
    queryKey: QUERY_KEYS.attendantsList,
    queryFn: async () => {
      const { data } = await api.get<Attendant[]>('/attendance-metrics/attendants-list')
      return data
    },
  })
}

// GET /attendance-metrics/snapshots/:id - Get snapshot with full dashboard data (F0035)
export function useSnapshot(id: string, options?: { enabled?: boolean }) {
  return useQuery<SnapshotDetail>({
    queryKey: QUERY_KEYS.snapshot(id),
    queryFn: async () => {
      const { data } = await api.get<SnapshotDetail>(`/attendance-metrics/snapshots/${id}`)
      return data
    },
    enabled: options?.enabled ?? !!id,
  })
}

// GET /attendance-metrics/snapshots/:id/export - Export snapshot CSV (F0035)
export function useExportSnapshotCsv() {
  return useMutation({
    mutationFn: async (snapshotId: string) => {
      const { data } = await api.get(`/attendance-metrics/snapshots/${snapshotId}/export`, {
        responseType: 'blob',
      })
      // Create download link
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `snapshot-${snapshotId}-${Date.now()}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
  })
}
